import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'
import type { ParkingPermit, PermitType, PermitStatus } from '@/types/parking'
import { vehicleKeys } from './useVehicles'
import { parkingZoneKeys } from './useParkingZones'

// Query keys
export const parkingPermitKeys = {
  all: ['parking_permits'] as const,
  lists: () => [...parkingPermitKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...parkingPermitKeys.lists(), filters] as const,
  details: () => [...parkingPermitKeys.all, 'detail'] as const,
  detail: (id: number) => [...parkingPermitKeys.details(), id] as const,
}

export interface PermitFilters {
  page?: number
  status?: PermitStatus
  permit_type?: PermitType
  q?: string
  // EP-97: server-side sort (allow-listed keys: updated_at/started_at/ended_at/slot_number)
  sort?: string
  sort_order?: string
}

// Fetch permits for a property
export function usePropertyPermits(propertyId: number, filters: PermitFilters = {}) {
  return useQuery({
    queryKey: parkingPermitKeys.list({ propertyId, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters.page) params.page = filters.page
      if (filters.status) params.status = filters.status
      if (filters.permit_type) params.permit_type = filters.permit_type
      if (filters.q) params.q = filters.q
      if (filters.sort) params.sort = filters.sort
      if (filters.sort_order) params.sort_order = filters.sort_order
      const { data } = await api.get<ApiResponse<ParkingPermit[]>>(
        `/api/properties/${propertyId}/parking_permits`,
        { params }
      )
      return data
    },
    enabled: !!propertyId,
  })
}

// EP-116 T028: a per-vehicle useVehiclePermits(vehicleId) hook used to live
// here — no consumer ever called it. VehicleListPage/VehicleFormPage both
// render permit status from the Vehicle record's own embedded
// `active_permits` array (already included in those pages' existing fetch),
// not a standalone per-vehicle permits query, and no vehicle detail page
// exists to need one. Removed 2026-08 (T028).

// Fetch single permit
export function usePermit(id: number) {
  return useQuery({
    queryKey: parkingPermitKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<ParkingPermit>>(`/api/parking_permits/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create permit
export interface CreatePermitInput {
  vehicle_id: number
  parking_zone_id?: number
  lease_id?: number
  permit_type: PermitType
  slot_number?: string
  sticker_number?: string
  monthly_fee?: number
  daily_fee?: number
  started_at: string
  ended_at?: string
  notes?: string
}

export function useCreatePermit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ vehicle_id, ...rest }: CreatePermitInput) => {
      const { data } = await api.post<ApiResponse<ParkingPermit>>(
        `/api/vehicles/${vehicle_id}/parking_permits`,
        { parking_permit: rest }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, parkingPermitKeys.lists())
      invalidateDetailQueries(queryClient, vehicleKeys.detail(data.vehicle_id))
      // WEB-M4: zone occupancy counts (occupied_slots/total_slots) live in the
      // zone list payload — a permit change makes them stale.
      queryClient.invalidateQueries({ queryKey: parkingZoneKeys.lists() })
    },
  })
}

// Update permit
export interface UpdatePermitInput {
  id: number
  parking_zone_id?: number | null
  lease_id?: number
  permit_type?: PermitType
  slot_number?: string | null
  sticker_number?: string | null
  monthly_fee?: number | null
  daily_fee?: number | null
  started_at?: string
  ended_at?: string | null
  status?: PermitStatus
  notes?: string | null
}

export function useUpdatePermit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdatePermitInput) => {
      const { data } = await api.patch<ApiResponse<ParkingPermit>>(
        `/api/parking_permits/${id}`,
        { parking_permit: input }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, parkingPermitKeys.lists())
      invalidateDetailQueries(queryClient, parkingPermitKeys.detail(data.id))
      invalidateDetailQueries(queryClient, vehicleKeys.detail(data.vehicle_id))
      queryClient.invalidateQueries({ queryKey: parkingZoneKeys.lists() }) // WEB-M4
    },
  })
}

// EP-116 T028: a hard-delete useDeletePermit() hook used to live here — no
// consumer ever called it. ParkingPermitListPage's only removal action is
// "cancel" (PATCH useUpdatePermit with { status: 'cancelled', ended_at }),
// a deliberate soft-cancel pattern that preserves the permit's history rather
// than hard-deleting it — there is no hard-delete affordance anywhere in the
// UI. The backend DELETE /api/parking_permits/:id endpoint itself is
// untouched (out of scope; still real, unguarded, just unconsumed). Removed
// 2026-08 (T028).
